import { useState, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Quote } from '../../../../../../Typescript/backend/classes';
import { useAuth } from '@calefy-inc/authentication';
import { useMutation } from '@apollo/client';
import { graphql } from '../../../../../../gql';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import { SupportLink } from '../../../../../common/SupportLink';
import { useUsingVagoSettings } from '../../../../../../hooks';

interface VagoResendSubmissionEmailButtonProps {
  quote: Quote;
}
export const VagoResendSubmissionEmailButton = ({
  quote,
}: VagoResendSubmissionEmailButtonProps) => {
  const onVago = useUsingVagoSettings();
  const { token } = useAuth();
  const [resendSubmissionEmail, { loading, error, reset }] = useMutation(
    VAGO_RESEND_SUBMISSION_EMAIL,
    {
      onCompleted: () => {
        enqueueSnackbar('Email successfully sent!', {
          variant: 'success',
        });
      },
    },
  );
  const { enqueueSnackbar } = useSnackbar();
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      setErrorDialogOpen(true);
    }
  }, [error]);

  if (!onVago) {
    return null;
  }

  return (
    <>
      <LoadingButton
        loading={loading}
        disabled={!token}
        onClick={() => {
          if (!token) {
            return;
          }
          resendSubmissionEmail({
            variables: {
              token,
              quoteUuid: quote.uniqueId,
            },
          });
        }}
      >
        Resend Submission Email
      </LoadingButton>
      <ErrorDialog
        open={errorDialogOpen}
        onOk={() => {
          setErrorDialogOpen(false);
          reset();
        }}
        error={error}
      />
    </>
  );
};

interface ErrorDialogProps {
  open: boolean;
  onOk: () => void;
  error?: Error;
}
const ErrorDialog = ({ open, onOk, error }: ErrorDialogProps) => {
  return (
    <Dialog open={open}>
      <DialogTitle>Error Resending Email</DialogTitle>
      <DialogContent>
        <Typography>
          We're sorry, but there was an unexpected error when re-sending the
          submission email. Please try refreshing the page and trying again. If
          the error persists, please get in touch with our dedicated support
          team at{' '}
          <SupportLink
            subject={`Error re-sending submission email: ${
              error ? error.message : 'Unknown error'
            }`}
          />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onOk()}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

const VAGO_RESEND_SUBMISSION_EMAIL = graphql(`
  mutation vagoResendSubmissionEmail($token: String!, $quoteUuid: String!) {
    vagoResendSubmissionEmail(token: $token, quoteUuid: $quoteUuid) {
      ok
    }
  }
`);
